import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/env.js';

const ConsultationHistory = ({ onViewReport, onBack, onNewConsultation }) => {
  const { user, authFetch } = useAuth();
  const [consultations, setConsultations] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nameFilter, setNameFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const selectedPatientIdRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    selectedPatientIdRef.current = selectedPatient?.id || null;
  }, [selectedPatient]);

  useEffect(() => {
    fetchConsultations();
  }, [nameFilter]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (nameFilter.trim()) {
        params.append('name', nameFilter.trim());
      }

      const response = await authFetch(getApiUrl(`/api/consultations/list?${params.toString()}`));
      const data = await response.json();

      if (data.success) {
        const allConsultations = data.consultations || [];
        setConsultations(allConsultations);

        // Group consultations by patient
        const patientMap = new Map();
        allConsultations.forEach(consultation => {
          const patientKey = `${consultation.patient_first_name}_${consultation.patient_last_name}_${consultation.patient_dob || ''}`;
          if (!patientMap.has(patientKey)) {
            patientMap.set(patientKey, {
              id: patientKey,
              firstName: consultation.patient_first_name,
              lastName: consultation.patient_last_name,
              fullName: consultation.patient_name,
              dob: consultation.patient_dob,
              sex: consultation.patient_sex,
              address: consultation.patient_address,
              consultations: [],
              lastConsultation: consultation.created_at
            });
          }
          patientMap.get(patientKey).consultations.push(consultation);
          // Update last consultation date if newer
          if (new Date(consultation.created_at) > new Date(patientMap.get(patientKey).lastConsultation)) {
            patientMap.get(patientKey).lastConsultation = consultation.created_at;
          }
        });

        // Convert to array and sort by last consultation date
        const patientList = Array.from(patientMap.values()).sort(
          (a, b) => new Date(b.lastConsultation) - new Date(a.lastConsultation)
        );
        setPatients(patientList);

        // Update selectedPatient if one is selected (to refresh after delete)
        if (selectedPatientIdRef.current) {
          const updatedPatient = patientList.find(p => p.id === selectedPatientIdRef.current);
          if (updatedPatient) {
            setSelectedPatient(updatedPatient);
          } else {
            // Patient no longer has consultations, go back to patient list
            setSelectedPatient(null);
          }
        }

        setError(null);
      } else {
        setError(data.message || 'Failed to fetch consultations');
      }
    } catch (err) {
      console.error('Error fetching consultations:', err);
      setError('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (consultationId) => {
    try {
      const response = await authFetch(getApiUrl('/api/consultations/delete'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          consultation_id: consultationId
        })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh the list
        fetchConsultations();
        setDeleteConfirm(null);
      } else {
        alert(data.message || 'Failed to delete consultation');
      }
    } catch (err) {
      console.error('Error deleting consultation:', err);
      alert('Failed to delete consultation');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateAge = (dateString) => {
    if (!dateString) return null;
    const birthDate = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleBackToPatients = () => {
    setSelectedPatient(null);
  };

  // Get consultations for selected patient - use spread to avoid mutating original array
  const patientConsultations = selectedPatient
    ? [...selectedPatient.consultations].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#fafbfc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
    }}>
      {/* Top Navigation Bar */}
      <nav style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            transition: 'opacity 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          title="Back to home"
        >
          <svg style={{ width: '32px', height: '32px', color: '#059669' }} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L21.5 7v10L12 22 2.5 17V7L12 2z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <path d="M9 9h6M12 6v12M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>MediScan</span>
        </button>

        <button
          onClick={selectedPatient ? handleBackToPatients : onBack}
          style={{
            padding: '8px 16px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#6b7280',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#f9fafb';
            e.target.style.borderColor = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'white';
            e.target.style.borderColor = '#e5e7eb';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          {selectedPatient ? 'Back to patients' : 'Back'}
        </button>
      </nav>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px',
        width: '100%'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: '32px' }}>
          {selectedPatient ? (
            <>
              {/* Breadcrumb */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                <button
                  onClick={handleBackToPatients}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#059669',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  My patients
                </button>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
                <span>{selectedPatient.fullName}</span>
              </div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>
                {selectedPatient.fullName}
              </h1>
              <p style={{
                fontSize: '15px',
                color: '#6b7280',
                margin: 0
              }}>
                {patientConsultations.length} consultation{patientConsultations.length !== 1 ? 's' : ''}
                {selectedPatient.dob && ` • Born on ${formatShortDate(selectedPatient.dob)}`}
              </p>
            </>
          ) : (
            <>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>
                My patients
              </h1>
              <p style={{
                fontSize: '15px',
                color: '#6b7280',
                margin: 0
              }}>
                {patients.length} patient{patients.length !== 1 ? 's' : ''} • {consultations.length} consultation{consultations.length !== 1 ? 's' : ''} total
              </p>
            </>
          )}
        </div>

        {/* Search Filter - only show on patient list */}
        {!selectedPatient && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            border: '1px solid #e5e7eb'
          }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Search for a patient
            </label>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Patient name..."
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#059669'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <button
                onClick={fetchConsultations}
                style={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(5, 150, 105, 0.15)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(5, 150, 105, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(5, 150, 105, 0.15)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                Search
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e5e7eb',
              borderTopColor: '#059669',
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{
              fontSize: '15px',
              color: '#6b7280',
              margin: 0
            }}>
              Loading...
            </p>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" style={{ margin: '0 auto 12px', display: 'block' }}>
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
            <p style={{
              fontSize: '15px',
              color: '#991b1b',
              margin: 0
            }}>
              {error}
            </p>
          </div>
        )}

        {/* Patient List View */}
        {!loading && !error && !selectedPatient && (
          <>
            {patients.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '60px 20px',
                textAlign: 'center',
                border: '1px solid #e5e7eb'
              }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" style={{ margin: '0 auto 16px', display: 'block' }}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                <p style={{
                  fontSize: '15px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  No patient found
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '16px'
              }}>
                {patients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#059669';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                      {/* Avatar */}
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{
                          color: 'white',
                          fontSize: '18px',
                          fontWeight: '600'
                        }}>
                          {patient.firstName?.charAt(0) || ''}{patient.lastName?.charAt(0) || ''}
                        </span>
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <h3 style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#111827',
                            margin: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {patient.fullName}
                          </h3>
                          {calculateAge(patient.dob) !== null && (
                            <span style={{
                              fontSize: '13px',
                              color: '#6b7280',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}>
                              {calculateAge(patient.dob)} yrs
                            </span>
                          )}
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}>
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                            </svg>
                            {patient.consultations.length} consultation{patient.consultations.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        <p style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                          margin: '8px 0 0 0'
                        }}>
                          Last visit: {formatShortDate(patient.lastConsultation)}
                        </p>
                      </div>

                      {/* New consultation button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          // Parse fullName if firstName/lastName are undefined
                          let firstName = patient.firstName;
                          let lastName = patient.lastName;
                          if ((!firstName || !lastName) && patient.fullName) {
                            const nameParts = patient.fullName.trim().split(/\s+/);
                            if (nameParts.length >= 2) {
                              firstName = firstName || nameParts[0];
                              lastName = lastName || nameParts.slice(1).join(' ');
                            } else if (nameParts.length === 1) {
                              firstName = firstName || nameParts[0];
                              lastName = lastName || '';
                            }
                          }
                          if (onNewConsultation) {
                            onNewConsultation({
                              nom: lastName || '',
                              prenom: firstName || '',
                              dateNaissance: patient.dob || '',
                              sexe: patient.sex || '',
                              adresse: patient.address || ''
                            });
                          }
                        }}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: '#059669',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                          color: 'white',
                          fontSize: '20px',
                          fontWeight: '400',
                          lineHeight: '1',
                          marginRight: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#047857';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#059669';
                        }}
                        title="New consultation with this patient"
                      >
                        +
                      </button>

                      {/* Arrow */}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}>
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Patient Consultations View */}
        {!loading && !error && selectedPatient && (
          <>
            {patientConsultations.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '60px 20px',
                textAlign: 'center',
                border: '1px solid #e5e7eb'
              }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" style={{ margin: '0 auto 16px', display: 'block' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <p style={{
                  fontSize: '15px',
                  color: '#6b7280',
                  margin: 0
                }}>
                  No consultation for this patient
                </p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {patientConsultations.map((consultation) => (
                  <div
                    key={consultation.id}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#059669';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexDirection: 'column',
                      gap: '16px'
                    }}>
                      <div style={{ width: '100%' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '8px',
                          flexWrap: 'wrap'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                              <line x1="16" y1="2" x2="16" y2="6"/>
                              <line x1="8" y1="2" x2="8" y2="6"/>
                              <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            <span style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#111827'
                            }}>
                              {formatDate(consultation.created_at)}
                            </span>
                          </div>
                          {consultation.status === 'COMPLETE' && (
                            <span style={{
                              background: '#ecfdf5',
                              color: '#059669',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              Complete
                            </span>
                          )}
                          {consultation.has_audio && (
                            <span style={{
                              background: '#eff6ff',
                              color: '#3b82f6',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                              </svg>
                              Audio
                            </span>
                          )}
                        </div>
                        {consultation.motif && (
                          <p style={{
                            fontSize: '14px',
                            color: '#4b5563',
                            margin: 0,
                            lineHeight: '1.5'
                          }}>
                            <span style={{ fontWeight: '500' }}>Reason:</span> {consultation.motif}
                          </p>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        width: '100%',
                        justifyContent: 'flex-start'
                      }}>
                        {/* View Report Button */}
                        <button
                          onClick={() => onViewReport(consultation.id)}
                          style={{
                            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 14px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                          </svg>
                          View report
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => setDeleteConfirm(consultation.id)}
                          style={{
                            background: 'white',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            padding: '8px 14px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.borderColor = '#f87171';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#fecaca';
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>
                Confirm deletion
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0,
                lineHeight: '1.5'
              }}>
                This action is irreversible. The consultation and all associated files will be deleted.
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  flex: 1,
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                style={{
                  flex: 1,
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#b91c1c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#dc2626';
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationHistory;
